/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import {
  ScheduleTaskTodoEventType,
  ScheduleType,
  UserSystemRole,
  userSystemRoleToBitSet,
} from '@gh/builder-shared-data';

import {
  EnumDictionary,
  FormState,
  FormStateBuilder,
  getDialogInitData,
  NumberTypes,
  whenCreateOrEditMode,
} from '@gh/common-shared';
import {
  AutocompleteLoadMoreEvent,
  DialogInput,
  FormMode,
  getControl,
  getControlValue,
  setControlValue,
  WaitingService,
} from '@gh/core-ui';

import {
  SchedulingScheduleTemplateTaskBusinessService,
} from '../../../resources/scheduling-schedule-template-task-business.service';
import {
  ScheduleTaskUserAutocomplete,
  ScheduleTemplateTaskTodoListItem,
} from '../../../resources/scheduling-schedule-template-task.model';

@Component({
  standalone: false,
  selector: 'gh-scheduling-schedule-template-task-todo-dialog',
  templateUrl: 'scheduling-schedule-template-task-todo-dialog.component.html',
  styleUrls: ['scheduling-schedule-template-task-todo-dialog.component.scss'],
})
export class SchedulingScheduleTemplateTaskDetailsTodoDialogComponent implements OnInit, DialogInput<ScheduleTemplateTaskTodoListItem> {
  input: any;
  form: FormState<ScheduleTemplateTaskTodoListItem>;
  systemRoles: EnumDictionary<UserSystemRole>;
  todoEventTypes: EnumDictionary<ScheduleTaskTodoEventType>;

  constructor(private dialogRef: MatDialogRef<any>,
              @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private waitingService: WaitingService,
              private businessService: SchedulingScheduleTemplateTaskBusinessService,
              private formStateBuilder: FormStateBuilder) {
  }

  get title(): string {
    return {
      [FormMode.CreateMode]: 'lbl_add_todo',
      [FormMode.EditMode]: 'lbl_edit_todo',
      [FormMode.ViewMode]: 'lbl_todo',
    }[this.form.mode];
  }

  ngOnInit(): void {
    const { systemRoles, salesSystemRoles, todoEventTypes } = getDialogInitData(this.dialogData);
    this.systemRoles = this.isSalesScheduleTemplate() ? salesSystemRoles : systemRoles;
    this.todoEventTypes = todoEventTypes;
    this.form = this.formStateBuilder.buildPlain({
      mode: this.input.mode,
      enabled: whenCreateOrEditMode(),
      value: {
        systemRole: this.input.systemRole,
        user: this.input.user,
        days: this.input.days,
        event: this.input.event,
        memo: this.input.memo,
      },
      children: {
        systemRole: void 0,
        user: void 0,
        days: { required: true, type: NumberTypes.Days },
        event: { required: true },
        memo: { required: true },
      },
    });
  }

  onSave(): void {
    if (this.form.group.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.dialogRef.close(this.form.submitValue);
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onSystemRoleChanged(): void {
    getControl(this.form.group, 'user').reset();
  }

  onSelectUser(user: Maybe<ScheduleTaskUserAutocomplete>): void {
    if (user) {
      setControlValue(this.form.group, 'user', {
        userName: user.userName,
        userId: user.userId,
      });
    }
  }

  loadAutocompleteUsers({ query, load }: AutocompleteLoadMoreEvent): void {
    load(this.businessService.getAutocompleteUser({
      ...query,
      filter: {
        name: query.filter,
        systemRoles: userSystemRoleToBitSet(getControlValue(this.form.group, 'systemRole')),
      },
    }));
  }

  private isSalesScheduleTemplate(): boolean {
    return this.input.scheduleTemplateType === ScheduleType.Sales;
  }
}
